'use client';

import ResponsiveModal from '@frontend/components/ResponsiveModal';
import type { ConnectedWallet, Wallet } from '@privy-io/react-auth';
import { useCreateWallet, usePrivy, useWallets } from '@privy-io/react-auth';
import { useSetActiveWallet } from '@privy-io/wagmi';
import { Button, Flex, Separator, Text } from '@radix-ui/themes';
import { FC, useMemo } from 'react';
import type { Address } from 'viem';
import { useAccount, useDisconnect } from 'wagmi';
import WalletListItem from './WalletListItem';

const WALLET_LABELS = {
  privy: 'Privy Wallet',
  metamask: 'MetaMask',
  coinbase: 'Coinbase Wallet',
  injected: 'Injected Wallet',
  walletconnect: 'WalletConnect',
} as const;

interface WalletManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WalletManagementModal: FC<WalletManagementModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { address } = useAccount();
  const { disconnect } = useDisconnect();
  const { logout } = usePrivy();
  const { wallets } = useWallets();
  const { setActiveWallet } = useSetActiveWallet();
  const { createWallet } = useCreateWallet({
    onSuccess: async (newWallet) => {
      try {
        // Immediately set the newly created wallet as active so Wagmi updates address without refresh
        await setActiveWallet(newWallet as unknown as ConnectedWallet);
      } catch {}
      onOpenChange(false);
    },
  });

  async function handleSwitchWallet(targetWallet: Wallet) {
    try {
      await setActiveWallet(targetWallet as unknown as ConnectedWallet);
      onOpenChange(false);
    } catch (e) {
      // no-op; UI shows connectError via connectError if needed
    }
  }

  const sortedWallets = useMemo(() => {
    if (!wallets) return [] as typeof wallets;
    const current = (address || '').toLowerCase();

    return [...wallets].sort((a, b) => {
      const aIsCurrent = (a.address || '').toLowerCase() === current;
      const bIsCurrent = (b.address || '').toLowerCase() === current;
      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;
      const aLabel =
        WALLET_LABELS[a.walletClientType] || a.walletClientType || '';
      const bLabel =
        WALLET_LABELS[b.walletClientType] || b.walletClientType || '';
      return aLabel.localeCompare(bLabel);
    });
  }, [wallets, address]);

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      mobileVariant="floatingIsland"
      desktopVariant="dialog"
    >
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center">
          <Text size="3" weight="bold">
            Wallets
          </Text>
          <Button
            size="1"
            radius="full"
            variant="soft"
            onClick={() => createWallet({ createAdditional: true })}
          >
            Create wallet
          </Button>
        </Flex>

        <Flex direction="column" gap="1">
          {sortedWallets?.map((w) => {
            const isCurrent =
              (address || '').toLowerCase() === (w.address || '').toLowerCase();
            const label =
              WALLET_LABELS[w.walletClientType] ||
              w.walletClientType ||
              'Wallet';

            return (
              <WalletListItem
                key={`${w.walletClientType}-${w.address}`}
                address={w.address as Address}
                isCurrent={isCurrent}
                label={label}
                onSwitch={() => handleSwitchWallet(w as unknown as Wallet)}
              />
            );
          })}
        </Flex>

        <Separator orientation="horizontal" size="4" className="opacity-20" />

        <Flex justify="between" align="center">
          <Button
            color="red"
            variant="soft"
            className="w-full!"
            radius="full"
            onClick={() => {
              disconnect();
              logout();
              onOpenChange(false);
            }}
          >
            Disconnect & Log out
          </Button>
        </Flex>
      </Flex>
    </ResponsiveModal>
  );
};

export default WalletManagementModal;
